import * as React from 'react';
import { debounce, offset, } from '@core-front/helper';
export const EqualHeightContext = React.createContext({
    addElementToGroup: () => null,
    groupHeights: {},
    triggerUpdate: () => null,
});
const EqualHeightProviderCore = (props) => {
    const { children, groups, offSet = 3, imageComponent, onGroupHeightsChange, } = props;
    const firstUpdate = React.useRef(true);
    const elementGroups = React.useRef({});
    const [groupHeights, setGroupHeights] = React.useState({});
    // Reset all GroupHeigts to empty object to reinitialize them
    const setAllGroupHeights = React.useCallback(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        setGroupHeights({});
    }, []);
    const handleResize = React.useCallback(() => {
        setAllGroupHeights();
    }, [setAllGroupHeights]);
    const debouncedResizeHandler = React.useMemo(() => debounce(handleResize, 100), [handleResize]);
    React.useEffect(() => {
        setAllGroupHeights();
    }, [setAllGroupHeights]);
    React.useEffect(() => {
        window.addEventListener('resize', debouncedResizeHandler);
        return () => {
            window.removeEventListener('resize', debouncedResizeHandler);
            debouncedResizeHandler.clear();
        };
    }, [debouncedResizeHandler]);
    const setGroupHeight = React.useCallback((groupKeys) => {
        // clone array since we're going to mutate it.
        const keys = [...groupKeys];
        // get first element from array and remove it
        const group = keys.shift();
        if (group) {
            if (!elementGroups.current[group]) {
                elementGroups.current[group] = [];
            }
            const elementGroup = elementGroups.current[group];
            // split elements in group by top offset
            const elementOffsetGroup = splitElementsByOffset(elementGroup, offSet);
            const heightsByElements = [];
            // assign every element to a group with it's height and offset
            for (const key of Object.keys(elementOffsetGroup)) {
                heightsByElements.push({
                    elements: elementOffsetGroup[key],
                    height: findTallestChildHeight(elementOffsetGroup[key]),
                    offsetTop: Number(key),
                });
            }
            // set group heights to state
            setGroupHeights((prevState) => (Object.assign(Object.assign({}, prevState), { [group]: heightsByElements })));
            // continue with next group
            setTimeout(() => {
                setGroupHeight(keys);
            });
            // Call onGroupHeightsChange after last groupHeight change
            if (keys.length === 0 && onGroupHeightsChange) {
                onGroupHeightsChange();
            }
        }
    }, [offSet, onGroupHeightsChange]);
    // If GroupHeights have been reset, set Heights again to all groups.
    React.useEffect(() => {
        if (Object.keys(groupHeights).length === 0) {
            setGroupHeight(groups);
        }
    }, [groupHeights, groups, setGroupHeight]);
    const addElementToGroup = React.useCallback((group, element) => {
        if (!elementGroups.current[group]) {
            elementGroups.current[group] = [];
        }
        elementGroups.current[group].push(element);
    }, [elementGroups]);
    return (React.createElement(EqualHeightContext.Provider, { value: {
            addElementToGroup,
            groupHeights,
            imageComponent,
            triggerUpdate: debouncedResizeHandler,
        } }, children));
};
export const findTallestChildHeight = (elements) => {
    return elements.reduce((accumulator, element) => {
        return element.scrollHeight > accumulator ? element.scrollHeight : accumulator;
    }, 0);
};
export const getElementGroup = (elementGroups, elementOffSet, offSet) => {
    const keys = Object.keys(elementGroups);
    // Default check if there is already group that's within +/-3px offset.
    // When page is zoomed offsets can have little differences
    return Number(keys.find((key) => {
        return (Number(key) >= elementOffSet - offSet && Number(key) <= elementOffSet + offSet);
    }));
};
export const splitElementsByOffset = (elements, offSet) => {
    const elementGroups = {};
    elements.forEach((element) => {
        if (element !== null) {
            const offsetTop = offset(element).top;
            const elementGroup = getElementGroup(elementGroups, offsetTop, offSet) || offsetTop;
            if (!elementGroups[elementGroup]) {
                elementGroups[elementGroup] = [];
            }
            elementGroups[elementGroup].push(element);
        }
    });
    return elementGroups;
};
export default EqualHeightProviderCore;
